import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRIVATEPATHS } from '@core/enums/paths.enum';
import integrationsService from 'services/api/integrations.service';

interface DataHealth {
    pharmacyGlnWithoutSgln?: number;
    supplierGlnWithoutSgln?: number;
    pharmaciesMissingPartyAddress?: number;
}

const EpcisReadinessPanel: FC = () => {
    const [health, setHealth] = useState<DataHealth | null>(null);
    const [strictSemantic, setStrictSemantic] = useState<boolean | null>(null);
    const [shipStrictMode, setShipStrictMode] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [healthRes, settingsRes]: any[] = await Promise.all([
                integrationsService.getEpcisDataHealth(),
                integrationsService.getPlatformSettings(),
            ]);
            setHealth(healthRes.data?.dataHealth ?? null);
            const settings: { key?: string; value?: string }[] = settingsRes.data?.platformSettings || [];
            const strict = settings.find((s) => s.key === 'epcis.validation.strict_semantic');
            setStrictSemantic(strict?.value === 'true' || strict?.value === '1');
            const shipStrict = settings.find((s) => s.key === 'epcis.ship.strictMode');
            setShipStrictMode(shipStrict?.value === 'true' || shipStrict?.value === '1');
        } catch {
            setHealth(null);
            setStrictSemantic(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                    EPCIS readiness
                </h4>
                <button
                    type="button"
                    onClick={load}
                    className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-white"
                >
                    Refresh
                </button>
            </div>
            {loading && <p className="text-sm text-slate-500">Loading data health…</p>}
            {!loading && health && (
                <ul className="space-y-2 text-sm text-slate-700">
                    <li>
                        Pharmacies missing SGLN:{' '}
                        <strong>{health.pharmacyGlnWithoutSgln ?? 0}</strong>
                        {(health.pharmacyGlnWithoutSgln ?? 0) > 0 && (
                            <Link to={PRIVATEPATHS.Users} className="ml-2 text-[#bd902e] hover:underline">
                                Fix in Users →
                            </Link>
                        )}
                    </li>
                    <li>
                        Suppliers missing SGLN:{' '}
                        <strong>{health.supplierGlnWithoutSgln ?? 0}</strong>
                        {(health.supplierGlnWithoutSgln ?? 0) > 0 && (
                            <Link to={PRIVATEPATHS.Supplier} className="ml-2 text-[#bd902e] hover:underline">
                                Fix in Suppliers →
                            </Link>
                        )}
                    </li>
                    <li>
                        Pharmacies missing ship-to address:{' '}
                        <strong>{health.pharmaciesMissingPartyAddress ?? 0}</strong>
                        {(health.pharmaciesMissingPartyAddress ?? 0) > 0 && (
                            <Link to={PRIVATEPATHS.Users} className="ml-2 text-[#bd902e] hover:underline">
                                Fix in Users →
                            </Link>
                        )}
                    </li>
                    <li>
                        Strict semantic validation:{' '}
                        <strong>{strictSemantic === true ? 'On' : strictSemantic === false ? 'Off' : 'Unknown'}</strong>
                        {strictSemantic === false && (
                            <span className="ml-2 text-amber-800">
                                — enable in Integrations for production
                            </span>
                        )}
                    </li>
                    <li>
                        Outbound ship strict mode:{' '}
                        <strong>{shipStrictMode === true ? 'On' : shipStrictMode === false ? 'Off' : 'Unknown'}</strong>
                        {shipStrictMode === false && (
                            <span className="ml-2 text-amber-800">
                                — enable in Integrations for production
                            </span>
                        )}
                    </li>
                </ul>
            )}
            <p className="mt-3 text-xs text-slate-500">
                <strong>100% compliance (supported formats):</strong> EPCIS XML 1.0/1.2/2.0 and JSON 2.0 validated L1→L4
                before SFTP/AS2 approve per{' '}
                <a href="/docs/EPCIS_COMPLIANCE.md" className="text-[#bd902e] hover:underline" target="_blank" rel="noreferrer">
                    OpenEPCIS
                </a>{' '}
                and{' '}
                <a href="https://www.hda.org/getmedia/f51620ca-ebbb-43d5-b192-99380d44433c/HDA-2023-TWS-1.pdf" className="text-[#bd902e] hover:underline" target="_blank" rel="noreferrer">
                    HDA TWS-1
                </a>
                . Production: strict semantic on, healthcare XSD fallback off.
            </p>
        </div>
    );
};

export default EpcisReadinessPanel;
